import { isPasskeySupported } from "@src/appV2/Auth/cognito/cognitoWebAuthnAuth";
import { getSsoMode } from "@src/appV2/Auth/RipplingSso/getSsoMode";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import {
  getPendingRememberEmailPrompt,
  REMEMBER_EMAIL_PROMPT_RESOLVED_EVENT,
} from "@src/appV2/Auth/rememberEmail/rememberEmailStore";
import { ManagePasskeysDialog } from "@src/appV2/redesign/MyAccount/components/ManagePasskeysDialog";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useEffect, useRef } from "react";

import { PasskeySetupPromptDialog } from "./PasskeySetupPromptDialog";
import {
  clearPendingPasskeySetupPrompt,
  hasPendingPasskeySetupPrompt,
  recordPasskeySetupPromptShown,
  shouldShowPasskeySetupPrompt,
} from "./passkeyPromptStore";

interface Props {
  isFeatureFlagReady: boolean;
  userEmail: string;
  userId: string;
}

export function PasskeySetupPromptManager(props: Props) {
  const { isFeatureFlagReady, userEmail, userId } = props;
  const promptModalState = useModalState();
  const managePasskeysModalState = useModalState();
  const { setModalStatus } = promptModalState;
  const hasHandledPrompt = useRef(false);
  const isPasskeyAuthEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_ADMIN_APP_PASSKEY_AUTH, {
    defaultValue: false,
  });

  useEffect(() => {
    function openPromptIfEligible(): void {
      if (
        !isFeatureFlagReady ||
        hasHandledPrompt.current ||
        !hasPendingPasskeySetupPrompt(userId) ||
        getPendingRememberEmailPrompt()
      ) {
        return;
      }
      hasHandledPrompt.current = true;

      if (!isPasskeyAuthEnabled || !isPasskeySupported() || getSsoMode(userEmail) === "sso_only") {
        clearPendingPasskeySetupPrompt();
        return;
      }

      if (!shouldShowPasskeySetupPrompt(userId)) {
        clearPendingPasskeySetupPrompt();
        return;
      }

      recordPasskeySetupPromptShown(userId);
      setModalStatus(ModalStatus.OPEN);
    }

    openPromptIfEligible();
    globalThis.addEventListener(REMEMBER_EMAIL_PROMPT_RESOLVED_EVENT, openPromptIfEligible);
    return () => {
      globalThis.removeEventListener(REMEMBER_EMAIL_PROMPT_RESOLVED_EVENT, openPromptIfEligible);
    };
  }, [isFeatureFlagReady, isPasskeyAuthEnabled, setModalStatus, userEmail, userId]);

  function handleSetUp(): void {
    promptModalState.closeModal();
    managePasskeysModalState.openModal();
  }

  return (
    <>
      <PasskeySetupPromptDialog
        modalState={promptModalState}
        onDismiss={promptModalState.closeModal}
        onSetUp={handleSetUp}
      />
      <ManagePasskeysDialog modalState={managePasskeysModalState} />
    </>
  );
}
